import React from "react";
import { Link } from "react-router-dom";

const Stars = ({ rating = 5 }) => (
    <div className="flex gap-1 text-yellow-400">
        {[...Array(5)].map((_, i) => (
            <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
        ))}
    </div>
);

/**
 * DigitalIdentitySection – A premium, responsive section showcasing Unitechnostack's
 * Digital Identity services. Designed with clarity, trust signals, and modern aesthetics.
 * All content is based on the provided copy, structured for SEO and visual impact.
 */
const DigitalIdentitySection = () => {
    const testimonials = [
        {
            name: "Alex Rivera",
            title: "Founder, Rivera Consulting",
            content:
                "Uniproid didn't just build me a website—they built my digital authority. Within weeks, I started receiving inbound inquiries from premium clients who mentioned my online presence.",
            avatar: "https://i.pravatar.cc/100?img=7",
        },
        {
            name: "Dr. Priya Sharma",
            title: "Executive Coach",
            content:
                "The strategic positioning workshop alone was worth the investment. My new site reflects exactly who I serve and how I help. It's like having a 24/7 sales representative.",
            avatar: "https://i.pravatar.cc/100?img=5",
        },
        {
            name: "Michael Chen",
            title: "Tech Advisor",
            content:
                "I've tried DIY platforms before. Nothing compares to the enterprise infrastructure and peace of mind Uniproid provides. My site is fast, secure, and always up‑to‑date.",
            avatar: "https://i.pravatar.cc/100?img=3",
        },
    ];
    return (


        // Inside your Uniproid component, replace the existing hero with this:

        <section className="relative min-h-screen flex items-center justify-center px-6 py-20 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            {/* Decorative blobs */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
            </div>

            <div className="relative max-w-7xl mx-auto w-full">
                {/* Main hero content */}
                <div className="text-center lg:text-left">
                    <span className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold tracking-wide mb-6">
                        Introducing Uniproid
                    </span>
                    <h1 className="text-5xl md:text-7xl font-extrabold leading-tight">
                        Your Professional Identity,
                        <br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-800 to-purple-900">
                            Built for Authority
                        </span>
                    </h1>
                    <p className="mt-6 text-xl text-gray-700 max-w-3xl mx-auto lg:mx-0">
                        Uniproid is the first platform that combines enterprise‑grade infrastructure, strategic positioning, and ongoing support — so you dominate search results and win trust before the first handshake.
                    </p>
                </div>

                {/* How It's Better (Comparison / Key Advantages) */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        {
                            title: "🚀 Beyond a Website",
                            desc: "Not just a template — a complete digital presence with hosting, SEO, and lead generation built‑in.",
                        },
                        {
                            title: "⚡ Enterprise Infrastructure",
                            desc: "Dedicated servers, global CDN, and bank‑level security. No shared hosting, no compromises.",
                        },
                        {
                            title: "🤝 Done‑for‑You Support",
                            desc: "Your identity is maintained, updated, and optimized by our team — you focus on your work.",
                        },
                    ].map((item, i) => (
                        <div key={i} className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-indigo-100">
                            <h3 className="text-2xl font-bold text-gray-900">{item.title}</h3>
                            <p className="mt-2 text-gray-600">{item.desc}</p>
                        </div>
                    ))}
                </div>

                {/* How to Use (3 Simple Steps) */}
                <div className="mt-20">
                    <h2 className="text-3xl font-bold text-center lg:text-left">Get Started in 3 Simple Steps</h2>
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { step: "1", title: "Discovery Call", desc: "We learn about your goals, audience, and expertise." },
                            { step: "2", title: "We Build & Optimize", desc: "Our team creates your custom site with all features." },
                            { step: "3", title: "Launch & Grow", desc: "Go live with ongoing support and continuous improvements." },
                        ].map((item, i) => (
                            <div key={i} className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold shadow-lg">
                                    {item.step}
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold">{item.title}</h3>
                                    <p className="text-gray-600">{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Benefits + Testimonial Row */}
                <div className="mt-20 grid lg:grid-cols-2 gap-10 items-center">
                    {/* Benefits list */}
                    <div>
                        <h2 className="text-3xl font-bold">What You Get</h2>
                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                                "✨ Custom authority website",
                                "🎯 Strategic narrative design",
                                "📈 SEO & lead capture ready",
                                "⚡ Lightning fast & secure",
                                "🛠️ Ongoing maintenance",
                                "🔒 Enterprise hosting",
                                "📊 Performance analytics",
                                "🤝 Priority support",
                            ].map((benefit, i) => (
                                <div key={i} className="flex items-center gap-3 text-gray-700">
                                    <span className="text-2xl">{benefit.split(' ')[0]}</span>
                                    <span className="font-medium">{benefit.slice(2)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial Card */}
                    <div className="bg-white rounded-3xl shadow-2xl p-8 border border-indigo-100">
                        <div className="flex items-center gap-2 text-yellow-400 mb-4">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                            ))}
                        </div>
                        <p className="text-xl text-gray-700 italic">
                            “Uniproid didn't just build a website — they built my digital authority. I now get inbound inquiries from premium clients who mention my online presence.”
                        </p>
                        <div className="mt-6 flex items-center gap-4">
                            <img src="https://i.pravatar.cc/60?img=7" alt="Alex Rim" className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md" />
                            <div>
                                <p className="font-semibold text-gray-900">Alex Rim</p>
                                <p className="text-sm text-gray-500">Founder, Rivera Consulting</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Buttons */}
                <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                    <Link
                        to="/free-trial"
                        className="px-10 py-5 bg-gray-900 text-white text-lg font-semibold rounded-2xl hover:bg-gray-800 transition shadow-2xl hover:shadow-indigo-200/50 transform hover:scale-105 text-center"
                    >
                        Start 30-Day Free Experience
                    </Link>

                </div>

                {/* Trust indicator */}
                <p className="mt-8 text-sm text-gray-500 text-center lg:text-left">
                    ⚡ Join hundreds of professionals • No credit card required
                </p>
            </div>

        </section>

    );
};

export default DigitalIdentitySection;